import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BusinessSegment } from '../models/tenant.model';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'CUSTOMER' | 'ADMIN';
  // Empresa a que a conta pertence
  tenant: { id: string; slug: string; name: string };
}

// Resposta do GET/PATCH /auth/me
export interface UserProfile extends Omit<AuthUser, 'tenant'> {
  // false nas contas criadas pelo Google que ainda não definiram senha
  hasPassword: boolean;
  // null enquanto o cliente não informou os dados de contato
  phone: string | null;
  address: string | null;
}

interface AuthResponse {
  access_token: string;
  user: AuthUser;
}

interface Session {
  token: string;
  user: AuthUser;
}

export interface CompanySignup {
  companyName: string;
  segment: BusinessSegment;
  slug: string;
  name: string;
  email: string;
  password: string;
  phone: string;
}

const SESSIONS_KEY = 'sessions';
// Contexto da plataforma/painel. As lojas usam o próprio slug (que nunca começa com "@").
export const ADMIN_CONTEXT = '@admin';

// Cada loja tem a sua sessão, e o painel do administrador tem outra: a mesma pessoa pode ser
// cliente de várias lojas (um cadastro em cada) sem que um login derrube o outro.
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/auth`;

  // Restaurado do localStorage para manter as sessões ao recarregar a página
  private readonly sessions = signal<Record<string, Session>>(this.readStoredSessions());

  // Onde o usuário está: ADMIN_CONTEXT ou o slug da loja. Definido pelos guards das rotas.
  private readonly currentContext = signal<string>(ADMIN_CONTEXT);
  readonly context = this.currentContext.asReadonly();
  // Slug da loja em que o usuário está (null na plataforma/painel)
  readonly storeSlug = computed(() => (this.currentContext() === ADMIN_CONTEXT ? null : this.currentContext()));

  readonly user = computed<AuthUser | null>(() => this.sessions()[this.currentContext()]?.user ?? null);
  readonly isAuthenticated = computed(() => this.user() !== null);
  readonly isAdmin = computed(() => this.user()?.role === 'ADMIN');
  // Administrador com a sessão do painel aberta, em qualquer contexto (ex.: vendo a própria loja)
  readonly adminUser = computed<AuthUser | null>(() => this.sessions()[ADMIN_CONTEXT]?.user ?? null);

  setContext(context: string) {
    this.currentContext.set(context);
  }

  googleLogin(googleToken: string) {
    return this.authenticate('google-login', { token: googleToken, ...this.tenantSlugField() });
  }

  // Login com email e senha: na loja entra o cliente dela; na plataforma, o administrador
  login(credentials: { email: string; password: string }) {
    return this.authenticate('login', { ...credentials, ...this.tenantSlugField() });
  }

  // Cadastro de cliente na loja atual (já devolve a sessão iniciada)
  register(data: { name: string; email: string; password: string }) {
    return this.authenticate('register', { ...data, ...this.tenantSlugField() });
  }

  // Cadastro de uma empresa: cria a empresa em trial e o seu administrador
  registerCompany(data: CompanySignup) {
    return this.authenticate('register-company', data);
  }

  isSlugAvailable(slug: string) {
    return this.http.get<{ available: boolean }>(`${this.apiUrl}/slug-available`, { params: { slug } });
  }

  private tenantSlugField(): { tenantSlug?: string } {
    const slug = this.storeSlug();
    return slug ? { tenantSlug: slug } : {};
  }

  private authenticate(endpoint: string, body: object) {
    return this.http.post<AuthResponse>(`${this.apiUrl}/${endpoint}`, body)
      .pipe(tap(response => this.storeSession(response)));
  }

  // O administrador fica sempre na sessão do painel, mesmo que entre pela página da própria loja
  private storeSession(response: AuthResponse) {
    const context = response.user.role === 'ADMIN' ? ADMIN_CONTEXT : response.user.tenant.slug;
    this.writeSessions({ ...this.sessions(), [context]: { token: response.access_token, user: response.user } });
  }

  // Dados da própria conta (tela "Meus dados")
  getProfile() {
    return this.http.get<UserProfile>(`${this.apiUrl}/me`);
  }

  updateProfile(data: { name: string; phone?: string; address?: string }) {
    return this.http.patch<UserProfile>(`${this.apiUrl}/me`, data).pipe(
      tap(profile => {
        // Mantém o nome do cabeçalho e da sessão guardada em dia
        const context = this.currentContext();
        const session = this.sessions()[context];
        if (!session) return;
        this.writeSessions({ ...this.sessions(), [context]: { ...session, user: { ...session.user, name: profile.name } } });
      })
    );
  }

  changePassword(data: { currentPassword?: string; newPassword: string }) {
    // A troca invalida os tokens antigos: a API devolve um novo para esta sessão continuar
    return this.http.patch<AuthResponse>(`${this.apiUrl}/me/password`, data)
      .pipe(tap(response => this.storeSession(response)));
  }

  // Token da sessão do contexto atual (cada requisição vai com o token da loja/painel em que o usuário está)
  getToken(): string | null {
    return this.sessions()[this.currentContext()]?.token ?? null;
  }

  getUserRole(): string {
    return this.user()?.role ?? 'CUSTOMER';
  }

  // Encerra apenas a sessão do contexto atual
  logout() {
    const { [this.currentContext()]: _removed, ...rest } = this.sessions();
    this.writeSessions(rest);
  }

  private writeSessions(sessions: Record<string, Session>) {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    this.sessions.set(sessions);
  }

  private readStoredSessions(): Record<string, Session> {
    try {
      const stored = JSON.parse(localStorage.getItem(SESSIONS_KEY) ?? '{}');
      if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
      // Descarta entradas ilegíveis em vez de derrubar todas as sessões
      return Object.fromEntries(
        Object.entries(stored as Record<string, Session>).filter(([, session]) => session?.token && session?.user?.id)
      );
    } catch {
      return {};
    }
  }
}
